import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Search, Filter, Grid, List, Heart, Bed, Bath, Square, MapPin, SlidersHorizontal, X, Save, Car, Home, Lock, UserPlus, GitCompare, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useCompare } from '../context/CompareContext';
import { ddfAPI } from '../services/api';
import { FEATURED_LISTINGS, PROPERTY_TYPES } from '../data/mockData';
import { toast } from 'sonner';

// Number of free property detail views for non-logged in users
const FREE_PROPERTY_VIEWS = 0;
const VIEWED_PROPERTIES_KEY = 'conti_viewed_properties';
const SEARCH_CACHE_KEY = 'conti_search_cache';
const SEARCH_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

// Common Ontario cities for spell checking
const KNOWN_CITIES = [
  'brantford', 'hamilton', 'burlington', 'oakville', 'mississauga', 'toronto',
  'cambridge', 'kitchener', 'waterloo', 'guelph', 'london', 'windsor',
  'st catharines', 'niagara falls', 'barrie', 'oshawa', 'ajax', 'pickering',
  'markham', 'vaughan', 'richmond hill', 'brampton', 'milton', 'ancaster',
  'dundas', 'stoney creek', 'grimsby', 'paris', 'st george', 'burford',
  'caledonia', 'hagersville', 'simcoe', 'norfolk', 'woodstock', 'stratford'
];

// Simple spell checker - find closest city match
const findClosestCity = (input) => {
  if (!input || input.length < 3) return null;
  const inputLower = input.toLowerCase().trim();
  
  // Check if exact match exists
  if (KNOWN_CITIES.includes(inputLower)) return null;
  
  // Calculate Levenshtein distance for fuzzy matching
  const levenshtein = (a, b) => {
    const matrix = [];
    for (let i = 0; i <= b.length; i++) matrix[i] = [i];
    for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        matrix[i][j] = b[i-1] === a[j-1] 
          ? matrix[i-1][j-1]
          : Math.min(matrix[i-1][j-1] + 1, matrix[i][j-1] + 1, matrix[i-1][j] + 1);
      }
    }
    return matrix[b.length][a.length];
  };
  
  let bestMatch = null;
  let bestDistance = Infinity;
  
  for (const city of KNOWN_CITIES) {
    const distance = levenshtein(inputLower, city);
    // Only suggest if distance is small enough (typo-like)
    if (distance <= 3 && distance < bestDistance && distance > 0) {
      bestDistance = distance;
      bestMatch = city;
    }
  }
  
  return bestMatch ? bestMatch.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : null;
};

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [listings, setListings] = useState([]);
  const [totalListings, setTotalListings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [useLiveData, setUseLiveData] = useState(false);
  const ITEMS_PER_PAGE = 100; // Reduced for faster initial load
  const navigate = useNavigate();
  
  // Default to Brantford area when no search params provided
  const defaultKeyword = 'Brantford';
  const hasSearchParams = searchParams.get('keyword') || searchParams.get('location') || 
    searchParams.get('propertyType') || searchParams.get('beds') || searchParams.get('baths') ||
    searchParams.get('minPrice') || searchParams.get('maxPrice');
  
  const [filters, setFilters] = useState({
    location: searchParams.get('location') || 'all',
    propertyType: searchParams.get('propertyType') || 'all',
    beds: searchParams.get('beds') || 'any',
    baths: searchParams.get('baths') || 'any',
    minPrice: searchParams.get('minPrice') || 'none',
    maxPrice: searchParams.get('maxPrice') || 'none',
    minSqft: searchParams.get('minSqft') || 'none',
    maxSqft: searchParams.get('maxSqft') || 'none',
    garage: searchParams.get('garage') || 'any',
    yearBuilt: searchParams.get('yearBuilt') || 'any',
    keyword: searchParams.get('keyword') || (hasSearchParams ? '' : defaultKeyword),
  });
  // Use ref for search input to avoid re-renders on every keystroke
  const searchInputRef = useRef(null);
  const [spellSuggestion, setSpellSuggestion] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const { user, isFavorite, addFavorite, removeFavorite, saveSearch } = useAuth();
  const { addToCompare, isInCompare, compareList } = useCompare();
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [viewedProperties, setViewedProperties] = useState([]);

  // Handle search submission - reads directly from ref (no state updates while typing)
  const handleSearch = useCallback(() => {
    const inputValue = searchInputRef.current?.value || '';
    // Check for spelling suggestion
    const suggestion = findClosestCity(inputValue);
    if (suggestion) {
      setSpellSuggestion(suggestion);
      return;
    }
    setSpellSuggestion(null);
    setFilters(prev => ({...prev, keyword: inputValue}));
  }, []);

  // Accept spelling suggestion
  const acceptSuggestion = useCallback(() => {
    if (searchInputRef.current) {
      searchInputRef.current.value = spellSuggestion;
    }
    setSpellSuggestion(null);
    setFilters(prev => ({...prev, keyword: spellSuggestion}));
  }, [spellSuggestion]);

  // Handle adding property to compare
  const handleAddToCompare = useCallback((e, property) => {
    e.preventDefault();
    e.stopPropagation();
    const result = addToCompare(property);
    if (result.success) {
      toast.success('Added to compare list');
    } else {
      toast.error(result.message);
    }
  }, [addToCompare]);

  // Load viewed properties from localStorage on mount
  useEffect(() => {
    if (!user) {
      const stored = localStorage.getItem(VIEWED_PROPERTIES_KEY);
      if (stored) {
        try {
          setViewedProperties(JSON.parse(stored));
        } catch (e) {
          setViewedProperties([]);
        }
      }
    }
  }, [user]);

  // Handle property click - check view limit for non-logged in users
  const handlePropertyClick = (e, propertyId) => {
    if (user) return; // Allow logged in users to view freely
    
    // Check if this property was already viewed
    if (viewedProperties.includes(propertyId)) {
      return; // Already viewed, allow access
    }
    
    // Check if user has reached the view limit
    if (viewedProperties.length >= FREE_PROPERTY_VIEWS) {
      e.preventDefault();
      setShowSignupModal(true);
      return;
    }
    
    // Track this view
    const newViewed = [...viewedProperties, propertyId];
    setViewedProperties(newViewed);
    localStorage.setItem(VIEWED_PROPERTIES_KEY, JSON.stringify(newViewed));
  };

  // Map location IDs to city names for DDF API
  const locationToCityMap = {
    'brantford': 'Brantford',
    'hamilton': 'Hamilton',
    'ancaster': 'Ancaster',
    'oakville': 'Oakville',
    'mississauga': 'Mississauga',
    'brant-county': 'Paris',
    'norfolk-county': 'Simcoe',
    'haldimand-county': 'Caledonia',
    'niagara-region': 'St. Catharines',
    'oxford-county': 'Woodstock'
  };

  // Get cached search results
  const getCachedSearch = useCallback((filterKey) => {
    try {
      const cached = localStorage.getItem(SEARCH_CACHE_KEY);
      if (cached) {
        const cache = JSON.parse(cached);
        if (cache[filterKey] && Date.now() - cache[filterKey].timestamp < SEARCH_CACHE_TTL) {
          return cache[filterKey].data;
        }
      }
    } catch (e) {}
    return null;
  }, []);

  // Set cached search results
  const setCachedSearch = useCallback((filterKey, data) => {
    try {
      let cache = {};
      const existing = localStorage.getItem(SEARCH_CACHE_KEY);
      if (existing) {
        cache = JSON.parse(existing);
      }
      // Keep only last 5 searches to avoid storage bloat
      const keys = Object.keys(cache);
      if (keys.length > 5) {
        delete cache[keys[0]];
      }
      cache[filterKey] = { data, timestamp: Date.now() };
      localStorage.setItem(SEARCH_CACHE_KEY, JSON.stringify(cache));
    } catch (e) {}
  }, []);

  // Fetch listings from API with caching
  useEffect(() => {
    const fetchListings = async () => {
      // Generate cache key from filters
      const filterKey = JSON.stringify({
        keyword: filters.keyword,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        beds: filters.beds,
        baths: filters.baths,
        propertyType: filters.propertyType
      });

      // Check cache first for instant results
      const cached = getCachedSearch(filterKey);
      if (cached) {
        setListings(cached);
        setTotalListings(cached.length);
        setUseLiveData(true);
        setHasMore(cached.length >= ITEMS_PER_PAGE);
        setLoading(false);
        return;
      }

      setLoading(true);
      setCurrentPage(1);
      setHasMore(true);
      try {
        // Build DDF API filters
        const ddfFilters = {
          limit: ITEMS_PER_PAGE,
        };
        
        // Use keyword as search query for broader Ontario-wide search
        if (filters.keyword && filters.keyword.trim()) {
          ddfFilters.search = filters.keyword.trim();
        }
        
        // Price filters
        if (filters.minPrice && filters.minPrice !== 'none') {
          ddfFilters.minPrice = parseInt(filters.minPrice);
        }
        if (filters.maxPrice && filters.maxPrice !== 'none') {
          ddfFilters.maxPrice = parseInt(filters.maxPrice);
        }
        
        // Beds/baths filters
        if (filters.beds && filters.beds !== 'any') {
          ddfFilters.beds = parseInt(filters.beds);
        }
        if (filters.baths && filters.baths !== 'any') {
          ddfFilters.baths = parseInt(filters.baths);
        }
        
        // Property type filter
        if (filters.propertyType && filters.propertyType !== 'all') {
          ddfFilters.propertyType = filters.propertyType;
        }
        
        // Try DDF API for live listings
        const data = await ddfAPI.getProperties(ddfFilters);
        if (data && data.properties && data.properties.length > 0) {
          // Only keep properties with valid prices
          const validListings = data.properties.filter(p => p.price > 0);
          setListings(validListings);
          setTotalListings(validListings.length);
          setUseLiveData(true);
          setHasMore(validListings.length >= ITEMS_PER_PAGE);
          // Cache the results
          setCachedSearch(filterKey, validListings);
        } else {
          // Fallback to mock data
          setListings(FEATURED_LISTINGS);
          setTotalListings(FEATURED_LISTINGS.length);
          setHasMore(false);
        }
      } catch (err) {
        console.log('Using mock data:', err);
        setListings(FEATURED_LISTINGS);
        setTotalListings(FEATURED_LISTINGS.length);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, [filters]);

  // Load more listings
  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const ddfFilters = {
        limit: ITEMS_PER_PAGE * nextPage, // Fetch more items
      };
      
      // Apply same filters
      if (filters.keyword && filters.keyword.trim()) {
        ddfFilters.search = filters.keyword.trim();
      }
      if (filters.minPrice && filters.minPrice !== 'none') {
        ddfFilters.minPrice = parseInt(filters.minPrice);
      }
      if (filters.maxPrice && filters.maxPrice !== 'none') {
        ddfFilters.maxPrice = parseInt(filters.maxPrice);
      }
      if (filters.beds && filters.beds !== 'any') {
        ddfFilters.beds = parseInt(filters.beds);
      }
      if (filters.baths && filters.baths !== 'any') {
        ddfFilters.baths = parseInt(filters.baths);
      }
      if (filters.propertyType && filters.propertyType !== 'all') {
        ddfFilters.propertyType = filters.propertyType;
      }
      
      const data = await ddfAPI.getProperties(ddfFilters);
      if (data && data.properties && data.properties.length > 0) {
        const validListings = data.properties.filter(p => p.price > 0);
        setListings(validListings);
        setTotalListings(validListings.length);
        setCurrentPage(nextPage);
        // Check if we got fewer than expected (no more to load)
        setHasMore(validListings.length >= ITEMS_PER_PAGE * nextPage);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.log('Error loading more:', err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  // Memoize filtered and sorted listings for performance
  const sortedListings = useMemo(() => {
    // Apply local filters for sqft, garage, year built
    const filtered = listings.filter(property => {
      // Square footage filter
      if (filters.minSqft && filters.minSqft !== 'none') {
        if ((property.sqft || 0) < parseInt(filters.minSqft)) return false;
      }
      if (filters.maxSqft && filters.maxSqft !== 'none') {
        if ((property.sqft || 0) > parseInt(filters.maxSqft)) return false;
      }
      
      // Year built filter
      if (filters.yearBuilt && filters.yearBuilt !== 'any') {
        if (!property.yearBuilt || property.yearBuilt < parseInt(filters.yearBuilt)) return false;
      }
      
      // Garage filter (check parking features or parking total)
      if (filters.garage && filters.garage !== 'any') {
        const garageCount = property.parkingTotal || 0;
        if (garageCount < parseInt(filters.garage)) return false;
      }
      
      return true;
    });

    // Sort listings
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'price-low': return a.price - b.price;
        case 'price-high': return b.price - a.price;
        case 'beds': return b.beds - a.beds;
        case 'sqft': return (b.sqft || 0) - (a.sqft || 0);
        default: return (a.daysOnMarket || 0) - (b.daysOnMarket || 0);
      }
    });
  }, [listings, filters.minSqft, filters.maxSqft, filters.yearBuilt, filters.garage, sortBy]);

  const handleFavorite = useCallback((e, propertyId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error('Please sign in to save favorites', {
        action: {
          label: 'Sign In',
          onClick: () => navigate('/login')
        }
      });
      return;
    }
    if (isFavorite(propertyId)) {
      removeFavorite(propertyId);
      toast.success('Removed from favorites');
    } else {
      addFavorite(propertyId);
      toast.success('Added to favorites');
    }
  }, [user, isFavorite, removeFavorite, addFavorite, navigate]);

  const handleSaveSearch = useCallback(() => {
    if (!user) {
      toast.error('Please sign in to save searches', {
        action: {
          label: 'Sign In',
          onClick: () => navigate('/login')
        }
      });
      return;
    }
    saveSearch(filters);
  }, [user, saveSearch, filters, navigate]);

  // Memoize PropertyCard for better performance
  const PropertyCard = memo(({ property, isListView = false }) => {
    // Format price display based on priceLabel
    const formatPrice = () => {
      if (property.priceLabel === 'contact' || property.price === 0) {
        return 'Contact for Price';
      }
      if (property.priceLabel === 'lease') {
        return `$${property.price.toLocaleString()}/mo`;
      }
      return `$${property.price.toLocaleString()}`;
    };

    const inCompare = isInCompare(property.id);

    return (
      <Link to={`/property/${property.id}`} onClick={(e) => handlePropertyClick(e, property.id)}>
        <Card className={`group overflow-hidden hover:shadow-xl transition-all duration-300 border-0 shadow-md ${isListView ? 'flex flex-row' : ''}`}>
          <div className={`relative overflow-hidden ${isListView ? 'w-72 h-48' : 'h-56'}`}>
            <img 
              src={property.image} 
              alt={property.address}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <Badge className={`absolute top-3 left-3 ${property.priceLabel === 'lease' ? 'bg-blue-600' : 'bg-green-600'}`}>
              {property.priceLabel === 'lease' ? 'For Lease' : property.status}
            </Badge>
            <div className="absolute top-3 right-3 flex gap-2">
              <button
                onClick={(e) => handleAddToCompare(e, property)}
                className={`p-2 rounded-full transition-colors shadow-lg ${inCompare ? 'bg-red-600 text-white' : 'bg-white/90 hover:bg-white text-gray-600'}`}
                title={inCompare ? 'In compare list' : 'Add to compare'}
              >
                <GitCompare size={16} />
              </button>
              <button
                onClick={(e) => handleFavorite(e, property.id)}
                className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors shadow-lg"
              >
                <Heart 
                  size={18} 
                  className={isFavorite(property.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}
                />
              </button>
            </div>
            <div className="absolute bottom-3 left-3">
              <div className="text-white text-xl font-bold drop-shadow-lg">
                {formatPrice()}
              </div>
            </div>
          </div>
          <CardContent className={`p-4 ${isListView ? 'flex-1' : ''}`}>
            <div className="flex items-start gap-2 mb-2">
              <MapPin size={14} className="text-red-600 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 group-hover:text-red-600 transition-colors">
                  {property.address}
                </h3>
                <p className="text-gray-500 text-sm">{property.city}, {property.province}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-gray-600 text-sm">
              <div className="flex items-center gap-1">
                <Bed size={14} />
                <span>{property.beds}</span>
              </div>
              <div className="flex items-center gap-1">
                <Bath size={14} />
                <span>{property.baths}</span>
              </div>
              {property.sqft > 0 && (
                <div className="flex items-center gap-1">
                  <Square size={14} />
                  <span>{property.sqft.toLocaleString()} sqft</span>
                </div>
              )}
            </div>
            {isListView && (
              <p className="text-gray-600 text-sm mt-3 line-clamp-2">{property.description}</p>
            )}
          </CardContent>
        </Card>
      </Link>
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Search Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search any city in Ontario, address, or MLS#"
                  defaultValue={searchParams.get('keyword') || (hasSearchParams ? '' : defaultKeyword)}
                  className="w-full h-11 pl-10 pr-24 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
                <Button 
                  size="sm" 
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-9 bg-red-600 hover:bg-red-700"
                  onClick={handleSearch}
                >
                  Search
                </Button>
              </div>
              
              {/* Spell Suggestion */}
              {spellSuggestion && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-center gap-2">
                  <AlertCircle className="text-amber-600 flex-shrink-0" size={18} />
                  <span className="text-sm text-amber-800">
                    Did you mean <button 
                      onClick={acceptSuggestion}
                      className="font-semibold text-amber-900 underline hover:text-red-600"
                    >
                      {spellSuggestion}
                    </button>?
                  </span>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    className="ml-auto h-7 text-xs"
                    onClick={() => {
                      setSpellSuggestion(null);
                      setFilters(prev => ({...prev, keyword: searchInputRef.current?.value || ''}));
                    }}
                  >
                    Search anyway
                  </Button>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="h-11">
                    <SlidersHorizontal size={18} className="mr-2" />
                    Filters
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Filter Properties</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-6">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Property Type</label>
                      <Select value={filters.propertyType} onValueChange={(v) => setFilters({...filters, propertyType: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PROPERTY_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Min Price</label>
                        <Select value={filters.minPrice} onValueChange={(v) => setFilters({...filters, minPrice: v})}>
                          <SelectTrigger>
                            <SelectValue placeholder="No Min" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Min</SelectItem>
                            {[200000, 300000, 400000, 500000, 600000, 700000, 800000, 1000000].map((p) => (
                              <SelectItem key={p} value={p.toString()}>${(p/1000)}K</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Max Price</label>
                        <Select value={filters.maxPrice} onValueChange={(v) => setFilters({...filters, maxPrice: v})}>
                          <SelectTrigger>
                            <SelectValue placeholder="No Max" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Max</SelectItem>
                            {[400000, 500000, 600000, 700000, 800000, 1000000, 1500000, 2000000].map((p) => (
                              <SelectItem key={p} value={p.toString()}>${(p/1000)}K</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Bedrooms</label>
                        <Select value={filters.beds} onValueChange={(v) => setFilters({...filters, beds: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <SelectItem key={n} value={n.toString()}>{n}+</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Bathrooms</label>
                        <Select value={filters.baths} onValueChange={(v) => setFilters({...filters, baths: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <SelectItem key={n} value={n.toString()}>{n}+</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Square Footage */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Min Sq Ft</label>
                        <Select value={filters.minSqft} onValueChange={(v) => setFilters({...filters, minSqft: v})}>
                          <SelectTrigger>
                            <SelectValue placeholder="No Min" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Min</SelectItem>
                            {[500, 750, 1000, 1250, 1500, 2000, 2500, 3000].map((s) => (
                              <SelectItem key={s} value={s.toString()}>{s.toLocaleString()} sqft</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Max Sq Ft</label>
                        <Select value={filters.maxSqft} onValueChange={(v) => setFilters({...filters, maxSqft: v})}>
                          <SelectTrigger>
                            <SelectValue placeholder="No Max" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Max</SelectItem>
                            {[1000, 1500, 2000, 2500, 3000, 4000, 5000, 7500].map((s) => (
                              <SelectItem key={s} value={s.toString()}>{s.toLocaleString()} sqft</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Garage & Year Built */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Garage</label>
                        <Select value={filters.garage} onValueChange={(v) => setFilters({...filters, garage: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="1">1+ Car</SelectItem>
                            <SelectItem value="2">2+ Car</SelectItem>
                            <SelectItem value="3">3+ Car</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Year Built</label>
                        <Select value={filters.yearBuilt} onValueChange={(v) => setFilters({...filters, yearBuilt: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="2020">2020 or newer</SelectItem>
                            <SelectItem value="2015">2015 or newer</SelectItem>
                            <SelectItem value="2010">2010 or newer</SelectItem>
                            <SelectItem value="2000">2000 or newer</SelectItem>
                            <SelectItem value="1990">1990 or newer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Clear Filters Button */}
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => {
                        if (searchInputRef.current) searchInputRef.current.value = '';
                        setSpellSuggestion(null);
                        setFilters({
                          location: 'all',
                          propertyType: 'all',
                          beds: 'any',
                          baths: 'any',
                          minPrice: 'none',
                          maxPrice: 'none',
                          minSqft: 'none',
                          maxSqft: 'none',
                          garage: 'any',
                          yearBuilt: 'any',
                          keyword: ''
                        });
                      }}
                    >
                      Clear All Filters
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40 h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="beds">Most Bedrooms</SelectItem>
                  <SelectItem value="sqft">Largest</SelectItem>
                </SelectContent>
              </Select>
              <div className="hidden md:flex border rounded-md">
                <Button 
                  variant={viewMode === 'grid' ? 'default' : 'ghost'} 
                  size="icon"
                  onClick={() => setViewMode('grid')}
                  className={viewMode === 'grid' ? 'bg-red-600 hover:bg-red-700' : ''}
                >
                  <Grid size={18} />
                </Button>
                <Button 
                  variant={viewMode === 'list' ? 'default' : 'ghost'} 
                  size="icon"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' ? 'bg-red-600 hover:bg-red-700' : ''}
                >
                  <List size={18} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-4">
          <p className="text-gray-600">
            <span className="font-semibold text-gray-900">{sortedListings.length}</span> active properties found
            {!user && (
              <span className="text-red-600 ml-2 font-medium">
                (Sign up to view property details)
              </span>
            )}
          </p>
          {user && (
            <Button variant="outline" onClick={handleSaveSearch} className="gap-2">
              <Save size={16} />
              Save Search
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading properties...</p>
          </div>
        ) : (
          <>
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
              : 'space-y-4'
            }>
              {/* Show ALL properties - signup modal appears when clicking after limit reached */}
              {sortedListings.map((property) => (
                <PropertyCard key={property.id} property={property} isListView={viewMode === 'list'} />
              ))}
            </div>
            
            {/* Load More Button */}
            {hasMore && sortedListings.length > 0 && useLiveData && (
              <div className="flex justify-center mt-8">
                <Button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-3"
                  data-testid="load-more-btn"
                >
                  {loadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    <>Load More Properties</>
                  )}
                </Button>
              </div>
            )}
            
            {/* Signup Modal for non-logged in users who reached view limit */}
            <Dialog open={showSignupModal} onOpenChange={setShowSignupModal}>
              <DialogContent className="sm:max-w-md">
                <div className="text-center p-4">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Create a Free Account
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Sign up for free to view property photos, details, save your favorites, and get notified about new listings.
                  </p>
                  <div className="space-y-3">
                    <Button 
                      className="w-full bg-red-600 hover:bg-red-700 text-white"
                      onClick={() => navigate('/register')}
                    >
                      <UserPlus size={18} className="mr-2" />
                      Create Free Account
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate('/login')}
                    >
                      Already have an account? Sign In
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-4">
                    Free forever. No credit card required.
                  </p>
                </div>
              </DialogContent>
            </Dialog>

            {sortedListings.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg">No properties found matching your criteria.</p>
                <Button variant="outline" className="mt-4" onClick={() => {
                  if (searchInputRef.current) searchInputRef.current.value = '';
                  setSpellSuggestion(null);
                  setFilters({
                    location: 'all', propertyType: 'all', beds: 'any', baths: 'any', minPrice: 'none', maxPrice: 'none', minSqft: 'none', maxSqft: 'none', garage: 'any', yearBuilt: 'any', keyword: ''
                  });
                }}>
                  Clear All Filters
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
